/* ==========================================================================
   Featured Clients — scroll-driven 3D cylinder wheel (sturdy.co style).
   The names sit at fixed positions around a cylinder and the whole wheel
   (the <ul>) rotates with scroll; the back half is culled purely with
   backface-visibility, so the visible half reads as a full curved wheel.
   Uses GSAP ScrollTrigger (loaded globally) to pin the section and map scroll
   progress to the wheel rotation. Progressive enhancement: without JS/GSAP or
   with prefers-reduced-motion, the section stays a static accessible list.
   ========================================================================== */
(function () {
  'use strict';

  var STEP = 18; // degrees between adjacent names on the cylinder
  var SPACING = 1; // arc spacing between names (1 = touching, matches sturdy at 100px)
  var LEAD = 80; // degrees the first name unfolds from (edge-on birth at the arrow line)
  var LEAD_ENTRY = 40; // portion of LEAD spent while the section scrolls into view — at
  // pin start the wheel is already at -(LEAD - LEAD_ENTRY) with the name visibly unfolding
  var TAIL = 36; // short exit spin past the last name, then the pin releases
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var controllers = new Map();

  function computeRadius(item) {
    var h = item ? item.offsetHeight : 40;
    var rad = (STEP * Math.PI) / 180;
    return (h / 2 / Math.tan(rad / 2)) * SPACING;
  }

  function setupStatic(section) {
    var items = section.querySelectorAll('.featured-clients__item');
    if (items[0]) items[0].classList.add('is-active');
    var live = section.querySelector('.featured-clients__live');
    if (live && items[0]) live.textContent = items[0].textContent.trim();
  }

  function setupEnhanced(section) {
    var items = Array.prototype.slice.call(
      section.querySelectorAll('.featured-clients__item')
    );
    if (!items.length) return null;

    var wheel = section.querySelector('.featured-clients__wheel');
    var live = section.querySelector('.featured-clients__live');
    var stage = section.querySelector('.featured-clients__stage');
    var arrows = Array.prototype.slice.call(
      section.querySelectorAll('.featured-clients__arrow')
    );
    var n = items.length;
    var radius = 240;
    var stageHalf = 300;
    var lastActive = -1;

    /* The side arrows leave early, like sturdy's sticky arrows being pushed
       out by the end of their wrapper: they slide up (scroll-linked) starting
       when the 2nd-to-last name reaches the front, and are gone shortly after
       the last one — the wheel keeps spinning while they exit. */
    var arrowExitStart = (n - 2) * STEP;
    var arrowExitEnd = (n - 1) * STEP; // per-breakpoint; desktop extends into the tail
    var arrowLift = true; // desktop only; on mobile the arrows stay put

    section.classList.add('featured-clients--enhanced');

    // Names are placed once around the cylinder; only the wheel rotates.
    function layout() {
      radius = computeRadius(items[0]);
      stageHalf = stage ? stage.offsetHeight / 2 : 300;
      for (var i = 0; i < n; i++) {
        items[i].style.transform =
          'rotateX(' + -i * STEP + 'deg) translateZ(' + radius + 'px)';
      }
    }

    function render(rotation) {
      wheel.style.transform = 'rotateX(' + rotation + 'deg)';
      var activeIndex = Math.round(rotation / STEP);
      var inRange = activeIndex >= 0 && activeIndex < n;
      for (var i = 0; i < n; i++) {
        items[i].classList.toggle('is-active', inRange && i === activeIndex);
      }
      if (inRange && activeIndex !== lastActive) {
        lastActive = activeIndex;
        if (live) live.textContent = items[activeIndex].textContent.trim();
      }
      if (arrows.length && n > 1 && arrowLift) {
        var t =
          (rotation - arrowExitStart) / (arrowExitEnd - arrowExitStart);
        t = Math.max(0, Math.min(1, t));
        var lift = t * (stageHalf + 40); // fully clear of the stage at t=1
        for (var a = 0; a < arrows.length; a++) {
          arrows[a].style.transform =
            'translateY(calc(-50% - ' + lift + 'px))';
        }
      }
    }

    function perItemPx() {
      var vh = parseFloat(section.dataset.scrollPerItem) || 40;
      return (vh / 100) * window.innerHeight;
    }

    /* Smoothing: a numeric `scrub` eases the playhead toward the scroll
       position instead of snapping 1:1, which is what removes the stepped
       feel of wheel/trackpad ticks. It only applies to a linked animation,
       so the wheel is driven by a tween over a proxy value rather than by
       reading self.progress in onUpdate. */
    function scrubAmount() {
      var value = parseFloat(section.dataset.scrollSmoothing);
      if (isNaN(value) || value <= 0) return true; // 0 = instant (old behaviour)
      return value;
    }

    var driver = { progress: 0 };

    var mm = window.gsap.matchMedia();

    // Desktop: the first name is born edge-on at -LEAD and unfolds at the
    // arrow line, sturdy-style. The invisible part of that birth
    // (-LEAD → -(LEAD - LEAD_ENTRY)) plays while the section scrolls into
    // view, so the moment it pins the name is already visibly unfolding — no
    // dead scroll, no blank stage. ONE scrubbed tween covers approach + pin
    // (piecewise mapping); the pin is a separate animation-less trigger.
    mm.add('(min-width: 750px)', function () {
      var pinRange = (LEAD - LEAD_ENTRY) + (n - 1) * STEP + TAIL;
      arrowExitEnd = (n - 1) * STEP + Math.min(18, TAIL);
      arrowLift = true;
      var pEntry = 0.5; // refreshed below

      function pinDistance() {
        return (pinRange / STEP) * perItemPx();
      }

      function update() {
        var p = driver.progress;
        var rotation;
        if (p < pEntry) {
          rotation = -LEAD + (p / pEntry) * LEAD_ENTRY;
        } else {
          rotation =
            -(LEAD - LEAD_ENTRY) + ((p - pEntry) / (1 - pEntry)) * pinRange;
        }
        render(rotation);
      }

      var tween = window.gsap.fromTo(
        driver,
        { progress: 0 },
        { progress: 1, ease: 'none', paused: true, onUpdate: update }
      );

      var stAnim = window.ScrollTrigger.create({
        trigger: section,
        start: 'top bottom',
        end: function () {
          return '+=' + (window.innerHeight + pinDistance());
        },
        animation: tween,
        scrub: scrubAmount(),
        invalidateOnRefresh: true,
        onRefresh: function () {
          pEntry =
            window.innerHeight / (window.innerHeight + pinDistance());
          layout();
          update();
        },
      });
      var stPin = window.ScrollTrigger.create({
        trigger: section,
        start: 'top top',
        end: function () {
          return '+=' + pinDistance();
        },
        pin: true,
        invalidateOnRefresh: true,
      });

      pEntry = window.innerHeight / (window.innerHeight + pinDistance());
      update();

      return function () {
        stAnim.kill(true);
        stPin.kill(true);
        tween.kill();
      };
    });

    mm.add('(max-width: 749px)', function () {
      // No pin on mobile (pinning reserves a large empty scroll gap). The wheel
      // scrubs from the first to the last name (no lead/tail) as the section
      // transits the viewport, anchored to its center so the whole rotation
      // happens while the section is comfortably in view.
      var total = (n - 1) * STEP;
      arrowLift = false;
      for (var a = 0; a < arrows.length; a++) {
        arrows[a].style.transform = '';
      }

      function update() {
        render(driver.progress * total);
      }

      var tween = window.gsap.fromTo(
        driver,
        { progress: 0 },
        { progress: 1, ease: 'none', paused: true, onUpdate: update }
      );
      var st = window.ScrollTrigger.create({
        trigger: section,
        start: 'center 75%',
        end: 'center 25%',
        animation: tween,
        scrub: scrubAmount(),
        invalidateOnRefresh: true,
        onRefresh: function () {
          layout();
          update();
        },
      });

      update();

      return function () {
        st.kill(true);
        tween.kill();
      };
    });

    layout();

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () {
        layout();
        window.ScrollTrigger.refresh();
      });
    }

    return {
      destroy: function () {
        try {
          mm.revert();
        } catch (e) {}
        section.classList.remove('featured-clients--enhanced');
      },
    };
  }

  function canEnhance() {
    return (
      !reduceMotion.matches &&
      window.gsap &&
      window.ScrollTrigger
    );
  }

  function initSection(section) {
    if (!section || controllers.has(section)) return;

    if (!canEnhance()) {
      setupStatic(section);
      controllers.set(section, null);
      return;
    }

    try {
      window.gsap.registerPlugin(window.ScrollTrigger);
    } catch (e) {}

    var ctrl = setupEnhanced(section);
    controllers.set(section, ctrl);
  }

  // GSAP is loaded with `defer`; poll briefly until it's ready, then init.
  function boot() {
    var sections = document.querySelectorAll('.featured-clients');
    if (!sections.length) return;

    var tries = 0;
    (function waitForGsap() {
      var ready = window.gsap && window.ScrollTrigger;
      if (ready || reduceMotion.matches || tries > 60) {
        sections.forEach(function (s) {
          initSection(s);
        });
        return;
      }
      tries++;
      requestAnimationFrame(waitForGsap);
    })();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor lifecycle.
  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target.querySelector('.featured-clients');
    if (section) initSection(section);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var section = event.target.querySelector('.featured-clients');
    if (section && controllers.has(section)) {
      var ctrl = controllers.get(section);
      if (ctrl && ctrl.destroy) ctrl.destroy();
      controllers.delete(section);
    }
  });
})();
